import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/Layout';
import { HomePage } from './pages/Home';
import { WorkshopDetailPage } from './pages/WorkshopDetail';
import { AdminPage } from './pages/Admin';
import { AdminNewPage } from './pages/AdminNew';
import { AdminEditPage } from './pages/AdminEdit';
import { AdminCrmPage } from './pages/AdminCrm';
import { AdminFeedbackPage } from './pages/AdminFeedback';
import { AdminPreviewPage } from './pages/AdminPreview';
import { CourseDiscountPage } from './pages/CourseDiscount';
import { NotFoundPage } from './pages/NotFound';
import { ToastProvider } from './components/Toast';
import { initSentry, Sentry } from './lib/sentry';
import { initAnalytics } from './lib/analytics';
import './index.css';

// Initialize Sentry before render. No-op when VITE_SENTRY_DSN is unset, so
// local dev / Sentry-free deploys don't need any config.
initSentry();
initAnalytics();

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="container-page py-24 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Something went wrong</h1>
          <p className="mt-2 text-sm text-ink-muted">
            The error has been reported. Try reloading — if it keeps happening let us know.
          </p>
          <div className="mt-6 flex justify-center gap-2">
            <button className="btn-secondary" onClick={() => resetError()}>Try again</button>
            <button className="btn-accent" onClick={() => window.location.reload()}>Reload</button>
          </div>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/w/:slug" element={<WorkshopDetailPage />} />
                <Route path="/course-discount" element={<CourseDiscountPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/crm" element={<AdminCrmPage />} />
                <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
                <Route path="/admin/new" element={<AdminNewPage />} />
                <Route path="/admin/workshops/:id" element={<AdminEditPage />} />
                <Route path="/admin/preview" element={<AdminPreviewPage />} />
                <Route path="/admin/preview/:slug" element={<WorkshopDetailPage adminPreview />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
